import { useCallback, useEffect, useState } from "react";
import { isActiveEmp } from "../lib/constants";
import { toast } from "sonner";
import { Fingerprint, Download, Loader2, Wifi, WifiOff, Link2, RefreshCw, Usb, Upload } from "lucide-react";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { saveBlob } from "../lib/download";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("ar-SA-u-nu-latn", { dateStyle: "short", timeStyle: "short" }) : "—");

const Unmatched = ({ rows, employees, onLinked }) => {
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState("");
  const link = async (uid) => {
    if (!sel[uid]) return;
    setBusy(uid);
    try { const r = await api.bioDeviceLink(uid, sel[uid]); toast.success(`تم الربط وتحديث ${r.days} يوم حضور`); onLinked(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الربط"); } finally { setBusy(""); }
  };
  return (
    <div className="space-y-2" data-testid="bio-unmatched-list">
      <p className="text-xs font-semibold text-amber-700">بصمات غير مطابقة ({rows.length}) — اربطها بالموظف الصحيح:</p>
      {rows.map((r) => (
        <div key={r.device_user_id} className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/60 p-2 text-xs" data-testid={`bio-unmatched-${r.device_user_id}`}>
          <span className="font-mono font-bold">#{r.device_user_id}</span>
          <span className="text-slate-500">{r.count} بصمة · آخرها {r.last}</span>
          <select value={sel[r.device_user_id] || ""} onChange={(e) => setSel({ ...sel, [r.device_user_id]: e.target.value })}
            className="h-8 flex-1 min-w-[140px] rounded-md border border-slate-200 bg-white px-2" data-testid={`bio-link-select-${r.device_user_id}`}>
            <option value="">اختر الموظف…</option>
            {employees.filter(isActiveEmp).map((e) => <option key={e.id} value={e.id}>{e.name}{e.employee_number ? ` (${e.employee_number})` : ""}</option>)}
          </select>
          <Button size="sm" className="h-8 gap-1" disabled={!sel[r.device_user_id] || !!busy} onClick={() => link(r.device_user_id)} data-testid={`bio-link-btn-${r.device_user_id}`}>
            {busy === r.device_user_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />} ربط
          </Button>
        </div>
      ))}
    </div>
  );
};

const UsbImport = ({ last, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const pick = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setBusy(true);
    try { const r = await api.bioUsbImport(f); setRes(r); toast.success(`تمت القراءة: ${r.new} بصمة جديدة`); onDone(); }
    catch (er) { toast.error(er?.response?.data?.detail || "تعذّر رفع الملف"); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-sky-200 bg-sky-50/50 p-3 space-y-2" data-testid="bio-usb-import">
      <p className="text-xs font-semibold text-sky-800 flex items-center gap-1.5"><Usb className="h-4 w-4" /> جهاز بدون شبكة (فلاشة USB)</p>
      <p className="text-[11px] text-slate-600">من الجهاز: MENU ← USB ← تنزيل سجلات الحضور، ثم ارفع الملف هنا (dat / txt / csv / xls / xlsx). رقم المستخدم في الجهاز = الرقم الوظيفي. رفع نفس الملف مرة ثانية لا يكرّر البصمات.</p>
      <label className={`inline-flex items-center gap-2 rounded-md px-3 h-9 text-sm font-medium text-white cursor-pointer ${busy ? "bg-sky-400" : "bg-sky-600 hover:bg-sky-700"}`} data-testid="bio-usb-upload-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} رفع ملف البصمات من الفلاشة
        <input type="file" accept=".dat,.txt,.csv,.xls,.xlsx" className="hidden" disabled={busy} onChange={pick} data-testid="bio-usb-file" />
      </label>
      {res && (
        <div className="text-xs text-slate-700 rounded-lg bg-white border p-2" data-testid="bio-usb-result">
          قُرئ {res.rows} سطر · جديد <b>{res.new}</b> · مكرر {res.duplicates} · أيام حضور محدّثة {res.days}
          {res.unmatched.length > 0 && <span className="text-amber-700"> · أرقام غير مطابقة: {res.unmatched.join("، ")}</span>}
        </div>
      )}
      {last && !res && <p className="text-[11px] text-slate-500" data-testid="bio-usb-last">آخر رفع: {fmt(last.at)} · {last.file} · {last.new} بصمة جديدة</p>}
    </div>
  );
};

export const BioDeviceSettings = () => {
  const [st, setSt] = useState(null);
  const [rows, setRows] = useState([]);
  const [emps, setEmps] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const s = await api.bioDeviceStatus(); setSt(s);
      if (s.unmatched) { setRows(await api.bioDeviceUnmatched()); if (!emps.length) setEmps(await api.listEmployees()); } else setRows([]);
    } catch { setSt({ configured: false }); }
  }, [emps.length]);
  useEffect(() => { load(); }, [load]);
  const download = async () => {
    if (st?.configured && !window.confirm("تنزيل نسخة جديدة يلغي رمز الربط القديم — البرنامج القديم يتوقف. متابعة؟")) return;
    setBusy(true);
    try { await saveBlob(await api.bioDeviceAgent(), "WorkZoneBio.zip"); toast.success("تم تنزيل برنامج الربط — فكّ الضغط وشغّله على كمبيوتر المكتب"); load(); }
    catch { toast.error("تعذّر التنزيل"); } finally { setBusy(false); }
  };
  const d = st?.agent?.device || {};
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid="bio-device-settings">
      <div className="flex items-center justify-between gap-2">
        <p className="font-heading font-bold text-slate-800 text-sm flex items-center gap-1.5"><Fingerprint className="h-4 w-4 text-emerald-600" /> برمجة نظام البصمة</p>
        <button onClick={load} className="text-slate-400 hover:text-slate-600" data-testid="bio-device-refresh"><RefreshCw className="h-4 w-4" /></button>
      </div>
      {st?.configured && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs" data-testid="bio-device-status">
          <div className={`rounded-lg p-2 flex items-center gap-1.5 font-semibold ${st.online ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
            {st.online ? <Wifi className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />} {st.online ? "متصل" : "غير متصل"}
          </div>
          <div className="rounded-lg bg-slate-50 p-2"><span className="text-slate-400">الجهاز: </span>{d.name || "—"} {d.ip ? `· ${d.ip}` : ""}</div>
          <div className="rounded-lg bg-slate-50 p-2"><span className="text-slate-400">آخر مزامنة: </span>{fmt(st.agent?.last_sync)}</div>
          <div className="rounded-lg bg-slate-50 p-2"><span className="text-slate-400">بصمات اليوم: </span><b>{st.today_punches}</b> · المسجلين بالجهاز {d.users ?? "—"}</div>
          {d.error && <div className="col-span-full rounded-lg bg-rose-50 p-2 text-rose-700" data-testid="bio-device-error">{d.error}</div>}
        </div>
      )}
      <ol className="list-decimal pr-5 text-xs text-slate-600 space-y-1">
        <li>وصّل جهاز البصمة (ZKTeco) بنفس شبكة كمبيوتر المكتب.</li>
        <li>نزّل برنامج الربط وشغّله: <b>start_windows.bat</b> لويندوز أو <b>start_mac_linux.sh</b> لماك/لينكس.</li>
        <li>البرنامج يجد الجهاز تلقائياً ويبرمجه: يضبط الوقت ويضيف الموظفين بأرقامهم الوظيفية.</li>
        <li>سجّل بصمة إصبع كل موظف على الجهاز مرة واحدة — بعدها البصمات تنزل في الحضور كل دقيقة.</li>
      </ol>
      <Button onClick={download} disabled={busy} className="w-full sm:w-auto gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="bio-device-download">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} {st?.configured ? "إعادة تنزيل برنامج الربط" : "إعداد تلقائي — تنزيل برنامج الربط"}
      </Button>
      <UsbImport last={st?.last_usb} onDone={load} />
      {rows.length > 0 && <Unmatched rows={rows} employees={emps} onLinked={load} />}
    </div>
  );
};
